import { createContext, useContext, useEffect, useMemo, useState, ReactNode, useCallback } from "react";
import { STORAGE_KEYS } from "@/lib/storageKeys";

/**
 * "View as user" — admin-only read-only proxy mode.
 *
 * When active, data hooks throughout the app can call `useEffectiveUserId()`
 * instead of using the real auth user id, so the admin sees the dashboard /
 * journey / dialogs as that user would.
 *
 * Mutations should be blocked while `isViewAs` is true (read-only).
 *
 * Persisted in sessionStorage so the mode survives navigation but not a new
 * tab / browser restart.
 */

const STORAGE_KEY = STORAGE_KEYS.viewAsUser;

export interface ViewAsTarget {
  id: string;
  email?: string;
  fullName?: string;
  childName?: string;
}

interface ViewAsContextValue {
  viewAsUser: ViewAsTarget | null;
  isViewAs: boolean;
  enterViewAs: (target: ViewAsTarget) => void;
  exitViewAs: () => void;
}

const ViewAsContext = createContext<ViewAsContextValue | null>(null);

function readStorage(): ViewAsTarget | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.id === "string") return parsed as ViewAsTarget;
    return null;
  } catch {
    return null;
  }
}

export function ViewAsProvider({ children }: { children: ReactNode }) {
  const [viewAsUser, setViewAsUser] = useState<ViewAsTarget | null>(() =>
    typeof window === "undefined" ? null : readStorage()
  );

  // Sync across tabs / accidental sessionStorage edits
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setViewAsUser(readStorage());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const enterViewAs = useCallback((target: ViewAsTarget) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(target));
    } catch {
      // ignore quota errors
    }
    setViewAsUser(target);
  }, []);

  const exitViewAs = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    setViewAsUser(null);
  }, []);

  const value = useMemo<ViewAsContextValue>(
    () => ({
      viewAsUser,
      isViewAs: !!viewAsUser,
      enterViewAs,
      exitViewAs,
    }),
    [viewAsUser, enterViewAs, exitViewAs]
  );

  return <ViewAsContext.Provider value={value}>{children}</ViewAsContext.Provider>;
}

export function useViewAs(): ViewAsContextValue {
  const ctx = useContext(ViewAsContext);
  if (!ctx) {
    // Provider missing — degrade gracefully so non-admin pages never crash
    return {
      viewAsUser: null,
      isViewAs: false,
      enterViewAs: () => {},
      exitViewAs: () => {},
    };
  }
  return ctx;
}

/**
 * Returns the user id that data hooks should query against — view-as target
 * if active, otherwise the real authenticated user id you pass in.
 */
export function useEffectiveUserId(realUserId: string | undefined): string | undefined {
  const { viewAsUser } = useViewAs();
  return viewAsUser?.id ?? realUserId;
}
